import type {
  Invoice,
  PlanLimits,
  PlanWarning,
  BillingState,
  UsageSummary,
  PaymentMethod,
  CreditBalance,
  CreditPricing,
  ActivePlanGrant,
  RawSubscription,
  SubscriptionWarning,
} from 'src/sections/settings/billing/types/billing';

import useSWR from 'swr';
import { useMemo, useContext, useCallback, createContext } from 'react';

import { fetcher, endpoints } from 'src/lib/axios';
import { useOrganisation } from 'src/organisation/context/provider';

import {
  normalizeInvoices,
  deriveBillingState,
  normalizePlanLimits,
  normalizeCanBuyPack,
  normalizeCurrentPlan,
  normalizeIsDevAccount,
  normalizePlanWarnings,
  normalizeSubscription,
  normalizeUsageSummary,
  normalizeCreditPricing,
  normalizePaymentMethod,
  normalizeCreditBalances,
  normalizeActivePlanGrant,
  deriveSubscriptionWarning,
} from './helpers';

type BillingContextType = {
  orgId: string | null;
  orgStatus: unknown;
  subscription: RawSubscription | null;
  invoices: Invoice[] | null;
  paymentMethod: PaymentMethod | null;
  serverCurrentPlan: string | null;
  isDevAccount: boolean;
  planLimits: PlanLimits | null;
  currentUsage: UsageSummary | null;
  warnings: PlanWarning[] | null;
  creditBalances: CreditBalance[];
  creditPricing: CreditPricing | null;
  /** Free-months grant currently running, from a coupon or a support comp. */
  activePlanGrant: ActivePlanGrant | null;
  /** Whether credit top-ups are allowed right now. Decided server-side. */
  canBuyPack: boolean;
  billingState: BillingState;
  subscriptionWarning: SubscriptionWarning;
  isBillingLoading: boolean;
  billingError: unknown;
  revalidateBilling: () => Promise<void>;
  revalidateOrgStatus: () => Promise<unknown>;
  revalidateInvoices: () => Promise<unknown>;
  revalidatePaymentMethod: () => Promise<unknown>;
};

const BillingContext = createContext<BillingContextType | null>(null);

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const { organizationId } = useOrganisation();
  const orgId = organizationId ? String(organizationId) : null;

  const {
    data: orgStatus,
    isLoading: isStatusLoading,
    error: statusError,
    mutate: mutateStatus,
  } = useSWR(orgId ? endpoints.billing.orgStatus : null, fetcher, {
    revalidateOnMount: true,
    revalidateOnFocus: true,
    revalidateIfStale: true,
    dedupingInterval: 0,
  });

  const {
    data: invoicesData,
    isLoading: isInvoicesLoading,
    mutate: mutateInvoices,
  } = useSWR(orgId ? endpoints.billing.invoices : null, fetcher, {
    onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 401 || status === 403) return;
      if (retryCount >= 2) return;
      setTimeout(() => revalidate({ retryCount }), 3000);
    },
  });

  const {
    data: paymentMethodData,
    mutate: mutatePaymentMethod,
  } = useSWR(orgId ? endpoints.billing.paymentMethod : null, fetcher, {
    onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 400 || status === 401 || status === 403) return;
      if (retryCount >= 1) return;
      setTimeout(() => revalidate({ retryCount }), 3000);
    },
  });

  const subscription = useMemo<RawSubscription | null>(() => {
    if (!orgStatus) return null;
    const raw = orgStatus as Record<string, unknown>;
    const sub = (raw.subscription || raw.Subscription || null) as Record<string, unknown> | null;
    if (!sub) return null;
    return normalizeSubscription(sub);
  }, [orgStatus]);

  const serverCurrentPlan = useMemo(() => normalizeCurrentPlan(orgStatus), [orgStatus]);
  const isDevAccount = useMemo(() => normalizeIsDevAccount(orgStatus), [orgStatus]);
  const invoices = useMemo(() => normalizeInvoices(invoicesData), [invoicesData]);
  const paymentMethod = useMemo(() => normalizePaymentMethod(paymentMethodData), [paymentMethodData]);
  const planLimits = useMemo(() => normalizePlanLimits(orgStatus), [orgStatus]);
  const currentUsage = useMemo(() => normalizeUsageSummary(orgStatus), [orgStatus]);
  const warnings = useMemo(() => normalizePlanWarnings(orgStatus), [orgStatus]);
  const creditBalances = useMemo(() => normalizeCreditBalances(orgStatus), [orgStatus]);
  const creditPricing = useMemo(() => normalizeCreditPricing(orgStatus), [orgStatus]);
  const activePlanGrant = useMemo(() => normalizeActivePlanGrant(orgStatus), [orgStatus]);
  const canBuyPack = useMemo(() => normalizeCanBuyPack(orgStatus), [orgStatus]);

  const billingState = useMemo(
    () => deriveBillingState(serverCurrentPlan, subscription, false),
    [serverCurrentPlan, subscription]
  );

  const subscriptionWarning = useMemo(
    () => deriveSubscriptionWarning(subscription, paymentMethod),
    [subscription, paymentMethod]
  );

  const revalidateBilling = useCallback(async () => {
    await Promise.all([mutateStatus(), mutateInvoices(), mutatePaymentMethod()]);
  }, [mutateStatus, mutateInvoices, mutatePaymentMethod]);

  const value = useMemo<BillingContextType>(
    () => ({
      orgId,
      orgStatus: orgStatus ?? null,
      subscription,
      invoices,
      paymentMethod,
      serverCurrentPlan,
      isDevAccount,
      planLimits,
      currentUsage,
      warnings,
      creditBalances,
      creditPricing,
      activePlanGrant,
      canBuyPack,
      billingState,
      subscriptionWarning,
      isBillingLoading: isStatusLoading || isInvoicesLoading,
      billingError: statusError ?? null,
      revalidateBilling,
      revalidateOrgStatus: mutateStatus,
      revalidateInvoices: mutateInvoices,
      revalidatePaymentMethod: mutatePaymentMethod,
    }),
    [
      orgId,
      orgStatus,
      subscription,
      invoices,
      paymentMethod,
      serverCurrentPlan,
      isDevAccount,
      planLimits,
      currentUsage,
      warnings,
      creditBalances,
      creditPricing,
      activePlanGrant,
      canBuyPack,
      billingState,
      subscriptionWarning,
      isStatusLoading,
      isInvoicesLoading,
      statusError,
      revalidateBilling,
      mutateStatus,
      mutateInvoices,
      mutatePaymentMethod,
    ]
  );

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling() {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
}

